import {Close as CloseIcon} from '@mui/icons-material';
import {Download as DownloadIcon} from '@mui/icons-material';
import {ZoomIn as ZoomInIcon} from '@mui/icons-material';
import {ZoomOut as ZoomOutIcon} from '@mui/icons-material';
import {
    Alert,
    AppBar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, {useCallback, useState, useEffect, useMemo, useRef} from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {PDFDocument} from 'pdf-lib';
import 'pdfjs-dist/web/pdf_viewer.css';

/**
 * Facteur de conversion PDF points → pixels CSS pour un zoom "100% navigateur".
 * 1 pt PDF = 1/72 pouce ; le navigateur affiche à 96 DPI → 96/72 ≈ 1.333
 */
const PDF_DPI_SCALE = 96 / 72;

/**
 * LinkService minimal pour pdf.js AnnotationLayer.
 * Ouvre tous les liens externes dans un nouvel onglet.
 */
const newTabLinkService = {
    externalLinkEnabled: true,
    addLinkAttributes(link, url) {
        link.href = url;
        link.title = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
    },
    getDestinationHash() {
        return '';
    },
    getAnchorUrl(anchor) {
        return anchor || '';
    },
    goToDestination() {},
    goToPage() {},
    page: 1,
    pagesCount: 0,
    rotation: 0,
    isInPresentationMode: false,
};

/**
 * Noms des champs de formulaire à traiter comme des dates (masque __/__/____).
 */
const DATE_FIELD_NAMES = new Set(['report_date']);

/**
 * Formate une suite de caractères en date __/__/____ en n'gardant que les chiffres
 * et en insérant automatiquement les "/" (max 8 chiffres → __/__/____).
 */
const formatDateInput = (raw) => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 8);
    let out = digits.slice(0, 2);
    if (digits.length > 2) out += '/' + digits.slice(2, 4);
    if (digits.length > 4) out += '/' + digits.slice(4, 8);
    return out;
};

/**
 * Attache un masque de saisie __/__/____ (chiffres uniquement + "/" automatiques)
 * sur les inputs de formulaire dont le nom correspond à un champ date.
 * @param {Array} annotations - annotations pdf.js de la page rendue
 */
const attachDateFieldMasks = (annotations) => {
    if (!annotations) return;
    annotations.forEach((annotation) => {
        if (
            annotation.subtype !== 'Widget' ||
            annotation.fieldType !== 'Tx' ||
            !DATE_FIELD_NAMES.has(annotation.fieldName)
        ) {
            return;
        }
        const el = document.getElementById(`pdfjs_internal_id_${annotation.id}`);
        if (!el || el.dataset.dateMaskAttached) return;
        el.dataset.dateMaskAttached = '1';
        el.setAttribute('inputmode', 'numeric');
        el.setAttribute('placeholder', '__/__/____');
        el.setAttribute('maxlength', '10');
        el.addEventListener('input', () => {
            const formatted = formatDateInput(el.value);
            if (el.value !== formatted) {
                el.value = formatted;
            }
        });
    });
};

// Configuration du worker PDF.js
// Le CDN ne marche pas sur Chrome Android, utiliser le worker local dans ce cas
const ua = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
const hasTouchScreen = typeof navigator !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
const hasSmallScreen = typeof window !== 'undefined' && window.innerWidth < 1024;
const isProbablyChromeAndroid = /chrome/i.test(ua) && (/android/i.test(ua) || (hasTouchScreen && hasSmallScreen));

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

/**
 * PdfViewerModal
 * Modale de visionneuse PDF avec support adaptatif selon le navigateur
 * Utilise pdf.js directement pour le rendu
 *
 * Logique de rendu :
 * - PDF interactif (formulaire) : object → iframe → pdfjs
 * - PDF classique : iframe → pdfjs
 *
 * @param {boolean} interactive - Le PDF contient un formulaire interactif
 * @param {boolean} disableNative - Force l'utilisation de pdfjs (désactive object/iframe)
 * @param {{message: string, action_label: string, onAction: Function}} notice - Bandeau
 *        d'information affiché sous l'en-tête, avec une action facultative
 */
const PdfViewerModal = ({
    open,
    onClose,
    file,
    title,
    subtitle,
    filename,
    documentKey,
    interactive = false,
    disableNative = false,
    notice = null,
}) => {
    const [numPages, setNumPages] = useState(null);
    const [scale, setScale] = useState(PDF_DPI_SCALE);
    const [pdfSource, setPdfSource] = useState(null);
    const [iframeUrl, setIframeUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Nombre de pages déjà rendues (pour affichage progressif)
    const [renderedPages, setRenderedPages] = useState(0);
    // Dimensions de la première page (pour réserver l'espace des pages non rendues)
    const [pageDimensions, setPageDimensions] = useState(null);
    // Debug info (affiché après long press sur fermer)
    const [showDebugInfo, setShowDebugInfo] = useState(false);
    const [debugLogs, setDebugLogs] = useState([]);

    // Référence au document PDF chargé
    const pdfDocRef = useRef(null);
    // Références aux canvas (un par page)
    const canvasRefs = useRef([]);
    // Références aux divs AnnotationLayer (un par page)
    const annotationDivRefs = useRef([]);
    // Références aux divs TextLayer (un par page) pour la sélection de texte
    const textLayerDivRefs = useRef([]);
    // Données d'annotations par page (index 0-based) pour mise à jour PDF au téléchargement
    const annotationsDataRef = useRef([]);
    // Tâches de rendu en cours
    const renderTasksRef = useRef([]);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Fonction helper pour logger et afficher dans debug
    const addDebugLog = useCallback((message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        const logEntry = {timestamp, message, type};
        setDebugLogs((prev) => [...prev.slice(-20), logEntry]); // Garder les 20 derniers
        // Toujours logger dans la console aussi
        if (type === 'error') {
            console.error(message);
        } else if (type === 'warn') {
            console.warn(message);
        } else {
            console.log(message);
        }
    }, []);

    /**
     * Détection des capacités du navigateur pour l'affichage PDF natif
     * - supportsNativePdf: true si le navigateur peut afficher des PDFs (object/iframe)
     * - Chrome Android et navigateurs mobiles ne supportent généralement pas
     */
    const browserCapabilities = useMemo(() => {
        if (typeof navigator === 'undefined') {
            return {supportsNativePdf: false, isMobileDevice: true};
        }

        const ua = navigator.userAgent.toLowerCase();

        // Détecter les appareils mobiles via plusieurs méthodes
        const hasAndroidInUA = /android/i.test(ua);
        const hasIOSInUA = /iphone|ipad|ipod/i.test(ua);
        // Détecter iPad récents qui masquent leur identité (Safari desktop mode)
        const isAppleWebkit = /applewebkit/i.test(ua) && !/chrome/i.test(ua);
        const isProbablyIPad = isAppleWebkit && navigator.maxTouchPoints > 1;
        const hasMobileInUA = /mobile/i.test(ua);

        // Détecter via écran tactile (mobile en mode desktop)
        const hasTouchScreen = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;

        // Un petit écran avec tactile = probablement mobile
        const hasSmallScreen = window.innerWidth < 1024;
        const isProbablyMobile = hasTouchScreen && hasSmallScreen;

        const isMobileDevice = hasAndroidInUA || hasIOSInUA || hasMobileInUA || isProbablyMobile;

        // Chrome sur Android ne supporte pas les PDFs natifs (même en mode desktop)
        const isChromeAndroid = (hasAndroidInUA || isProbablyMobile) && /chrome/i.test(ua);

        // iOS/iPad : forcer pdfjs pour tous les navigateurs (Safari, Chrome, etc.)
        // Inclut les iPads récents en mode desktop (AppleWebkit + touchpoints)
        const isIOS = hasIOSInUA || isProbablyIPad;

        // Firefox Android a un support limité
        const isFirefoxAndroid = (hasAndroidInUA || isProbablyMobile) && /firefox/i.test(ua);

        // Les navigateurs desktop supportent généralement les PDFs natifs
        // iOS et Chrome Android sont forcés en pdfjs
        const supportsNativePdf = !isChromeAndroid && !isIOS && !isFirefoxAndroid && !isMobileDevice;

        return {supportsNativePdf, isMobileDevice, isIOS: hasIOSInUA};
    }, []);

    /**
     * Déterminer le mode de rendu :
     * - 'object' : balise object (meilleur pour formulaires interactifs)
     * - 'iframe' : balise iframe (bon support desktop)
     * - 'pdfjs' : rendu JavaScript (fallback universel)
     */
    const renderMode = useMemo(() => {
        // Force pdfjs si disableNative est activé
        if (disableNative) {
            return 'pdfjs';
        }

        if (interactive) {
            // PDF avec formulaire : object → iframe → pdfjs
            if (browserCapabilities.supportsNativePdf) {
                return 'object';
            }
        } else {
            // PDF classique : iframe → pdfjs
            if (browserCapabilities.supportsNativePdf) {
                return 'iframe';
            }
        }
        // Fallback : pdfjs (fonctionne partout)
        return 'pdfjs';
    }, [interactive, disableNative, browserCapabilities.supportsNativePdf]);

    // Préparer la source du PDF
    useEffect(() => {
        if (file) {
            setLoading(true);
            setError(null);

            if (typeof file === 'string' && (file.startsWith('blob:') || file.startsWith('http'))) {
                setPdfSource(file);
                setIframeUrl(file);
            } else if (typeof file === 'object' && file.data) {
                // Si c'est un objet {data: Uint8Array}
                setPdfSource({data: file.data});
                // Créer un Blob URL pour l'iframe
                const blob = new Blob([file.data], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);
                setIframeUrl(url);
                // Cleanup
                return () => URL.revokeObjectURL(url);
            } else {
                setPdfSource(null);
                setIframeUrl(null);
            }
        } else {
            setPdfSource(null);
            setIframeUrl(null);
            setNumPages(null);
        }
    }, [file, documentKey]);

    // Charger le document PDF avec pdf.js
    useEffect(() => {
        if (!pdfSource || renderMode !== 'pdfjs') {
            return;
        }

        let cancelled = false;

        const loadPdf = async () => {
            try {
                setLoading(true);
                setError(null);

                // Annuler tous les rendus en cours
                renderTasksRef.current.forEach((task) => {
                    if (task) task.cancel();
                });
                renderTasksRef.current = [];

                addDebugLog(
                    `[PDF] Chargement du document... ${typeof pdfSource === 'string' ? pdfSource : 'binary data'}`,
                );

                let pdfData;

                // Si c'est une URL (interne, externe, ou blob), la passer directement à pdf.js
                if (typeof pdfSource === 'string') {
                    addDebugLog('[PDF] Chargement URL directement par pdf.js');
                    pdfData = {url: pdfSource};
                } else {
                    // Données binaires directes
                    addDebugLog('[PDF] Chargement données binaires');
                    pdfData = {data: pdfSource.data};
                }

                if (cancelled) return;

                // Charger le document avec pdf.js
                const loadingTask = pdfjsLib.getDocument({
                    ...pdfData,
                    cMapUrl: '/cmaps/',
                    cMapPacked: true,
                });

                const pdfDoc = await loadingTask.promise;
                addDebugLog(`[PDF] Document chargé, pages: ${pdfDoc.numPages}`);

                if (cancelled) {
                    pdfDoc.destroy();
                    return;
                }

                // Pré-calculer les dimensions de la première page (optionnel, ne bloque pas si erreur)
                try {
                    const firstPage = await pdfDoc.getPage(1);
                    const baseWidth = isMobile ? window.innerWidth - 32 : undefined;
                    let firstViewport;
                    if (baseWidth) {
                        const unscaledViewport = firstPage.getViewport({scale: 1});
                        const scaleToFit = baseWidth / unscaledViewport.width;
                        firstViewport = firstPage.getViewport({scale: scaleToFit * scale});
                    } else {
                        firstViewport = firstPage.getViewport({scale: scale});
                    }
                    setPageDimensions({
                        width: Math.floor(firstViewport.width),
                        height: Math.floor(firstViewport.height),
                    });
                } catch (dimErr) {
                    addDebugLog(
                        `[PDF] Pré-calcul dimensions échoué (non bloquant): ${dimErr.message || dimErr}`,
                        'warn',
                    );
                }

                pdfDocRef.current = pdfDoc;
                setNumPages(pdfDoc.numPages);
                setLoading(false);
            } catch (err) {
                if (cancelled) return;
                addDebugLog(`[PDF] Erreur de chargement: ${err.message || err}`, 'error');
                // Message d'erreur plus informatif
                let errorMsg = 'Impossible de charger le PDF';
                if (err.message?.includes('Missing PDF')) {
                    errorMsg = 'Le fichier PDF est introuvable';
                } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
                    errorMsg = 'Erreur réseau lors du chargement';
                } else if (err.message?.includes('password')) {
                    errorMsg = 'Ce PDF est protégé par mot de passe';
                }
                setError(errorMsg);
                setLoading(false);
            }
        };

        loadPdf();

        return () => {
            cancelled = true;
            renderTasksRef.current.forEach((task) => {
                if (task) task.cancel();
            });
            renderTasksRef.current = [];
            if (pdfDocRef.current) {
                pdfDocRef.current.destroy();
                pdfDocRef.current = null;
            }
        };
    }, [pdfSource, renderMode]);

    // Rendre toutes les pages progressivement
    useEffect(() => {
        if (!pdfDocRef.current || renderMode !== 'pdfjs' || !numPages || loading) {
            return;
        }

        let cancelled = false;

        const renderAllPages = async () => {
            // Petit délai pour s'assurer que les canvas sont montés
            await new Promise((resolve) => setTimeout(resolve, 0));

            if (cancelled) return;

            try {
                // Annuler tous les rendus en cours
                renderTasksRef.current.forEach((task) => {
                    if (task) task.cancel();
                });
                renderTasksRef.current = [];
                setRenderedPages(0);
                // Vider les couches d'annotations et de texte existantes
                annotationDivRefs.current.forEach((div) => {
                    if (div) div.replaceChildren();
                });
                textLayerDivRefs.current.forEach((div) => {
                    if (div) div.replaceChildren();
                });

                for (let pageNum = 1; pageNum <= numPages; pageNum++) {
                    if (cancelled) return;

                    const canvas = canvasRefs.current[pageNum - 1];
                    if (!canvas) {
                        addDebugLog(`Canvas pour page ${pageNum} non trouvé`, 'warn');
                        continue;
                    }

                    const page = await pdfDocRef.current.getPage(pageNum);

                    if (cancelled) return;

                    // Calculer la largeur selon le mode (mobile ou desktop)
                    const baseWidth = isMobile ? window.innerWidth - 32 : undefined;
                    let viewport;

                    if (baseWidth) {
                        // Sur mobile, ajuster à la largeur
                        const unscaledViewport = page.getViewport({scale: 1});
                        const scaleToFit = baseWidth / unscaledViewport.width;
                        viewport = page.getViewport({scale: scaleToFit * scale});
                    } else {
                        viewport = page.getViewport({scale: scale});
                    }

                    // Mettre à jour les dimensions pour les placeholders (page 1 ou à chaque changement de scale)
                    if (pageNum === 1) {
                        setPageDimensions({
                            width: Math.floor(viewport.width),
                            height: Math.floor(viewport.height),
                        });
                    }

                    const context = canvas.getContext('2d');

                    // Supporter les écrans haute résolution
                    const outputScale = window.devicePixelRatio || 1;
                    canvas.width = Math.floor(viewport.width * outputScale);
                    canvas.height = Math.floor(viewport.height * outputScale);
                    canvas.style.width = `${Math.floor(viewport.width)}px`;
                    canvas.style.height = `${Math.floor(viewport.height)}px`;

                    const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

                    const renderContext = {
                        canvasContext: context,
                        transform: transform,
                        viewport: viewport,
                    };

                    const renderTask = page.render(renderContext);
                    renderTasksRef.current[pageNum - 1] = renderTask;

                    await renderTask.promise;

                    if (cancelled) return;

                    renderTasksRef.current[pageNum - 1] = null;

                    // Rendre la couche de texte (sélection de texte) via pdf.js
                    const textLayerDiv = textLayerDivRefs.current[pageNum - 1];
                    if (textLayerDiv && !cancelled) {
                        try {
                            textLayerDiv.replaceChildren();
                            const textContent = await page.getTextContent();
                            if (!cancelled) {
                                const textLayer = new pdfjsLib.TextLayer({
                                    textContentSource: textContent,
                                    container: textLayerDiv,
                                    viewport,
                                });
                                await textLayer.render();
                            }
                        } catch (_textErr) {
                            // Non bloquant
                        }
                    }

                    // Rendre la couche d'annotations (liens + champs de formulaire) via pdf.js
                    const annotationDiv = annotationDivRefs.current[pageNum - 1];
                    if (annotationDiv && !cancelled) {
                        try {
                            annotationDiv.replaceChildren();
                            // Définir le facteur d'échelle attendu par setLayerDimensions
                            annotationDiv.style.setProperty('--scale-factor', viewport.scale);
                            const annotations = await page.getAnnotations();
                            if (!cancelled && annotations.length > 0) {
                                const annotationLayer = new pdfjsLib.AnnotationLayer({
                                    div: annotationDiv,
                                    page,
                                    viewport,
                                });
                                await annotationLayer.render({
                                    annotations,
                                    viewport,
                                    linkService: newTabLinkService,
                                    renderForms: true,
                                });
                                // Stocker les données d'annotations pour la mise à jour au téléchargement
                                annotationsDataRef.current[pageNum - 1] = annotations;

                                // Appliquer un masque de saisie sur les champs date (__/__/____)
                                attachDateFieldMasks(annotations);
                            }
                        } catch (_annotErr) {
                            // Non bloquant
                        }
                    }

                    // Mettre à jour le nombre de pages rendues pour affichage progressif
                    setRenderedPages(pageNum);
                }
            } catch (err) {
                if (err.name === 'RenderingCancelledException') {
                    return; // Ignoré - rendu annulé normalement
                }
                addDebugLog(`Erreur de rendu des pages: ${err.message || err}`, 'error');
            }
        };

        renderAllPages();

        return () => {
            cancelled = true;
            renderTasksRef.current.forEach((task) => {
                if (task) task.cancel();
            });
            renderTasksRef.current = [];
        };
    }, [scale, isMobile, renderMode, numPages, loading]);

    const handleClose = useCallback(() => {
        // Nettoyer les ressources pdf.js
        renderTasksRef.current.forEach((task) => {
            if (task) task.cancel();
        });
        renderTasksRef.current = [];
        if (pdfDocRef.current) {
            pdfDocRef.current.destroy();
            pdfDocRef.current = null;
        }
        setPdfSource(null);
        setIframeUrl(null);
        setNumPages(null);
        setRenderedPages(0);
        annotationDivRefs.current.forEach((div) => {
            if (div) div.replaceChildren();
        });
        textLayerDivRefs.current.forEach((div) => {
            if (div) div.replaceChildren();
        });
        annotationsDataRef.current = [];
        setPageDimensions(null);
        setScale(PDF_DPI_SCALE);
        setError(null);
        onClose();
    }, [onClose]);

    const zoomIn = useCallback(() => {
        setScale((prev) => Math.min(prev + 0.25 * PDF_DPI_SCALE, 4 * PDF_DPI_SCALE));
    }, []);

    const zoomOut = useCallback(() => {
        setScale((prev) => Math.max(prev - 0.25 * PDF_DPI_SCALE, 0.25 * PDF_DPI_SCALE));
    }, []);

    const handleDownload = useCallback(async () => {
        if (!pdfSource) return;

        // En mode pdfjs, relire les valeurs des champs de formulaire depuis le DOM
        // et les ré-injecter dans le PDF via pdf-lib avant de télécharger
        if (renderMode === 'pdfjs' && annotationsDataRef.current.length > 0) {
            try {
                // Collecter les valeurs courantes depuis l'AnnotationLayer HTML
                const fieldValues = [];
                annotationDivRefs.current.forEach((div, pageIndex) => {
                    if (!div) return;
                    const pageAnnotations = annotationsDataRef.current[pageIndex];
                    if (!pageAnnotations) return;
                    pageAnnotations.forEach((annotation) => {
                        if (!annotation.fieldName || annotation.subtype !== 'Widget') return;
                        const el = document.getElementById(`pdfjs_internal_id_${annotation.id}`);
                        if (!el) return;
                        if (annotation.fieldType === 'Tx') {
                            fieldValues.push({name: annotation.fieldName, type: 'text', value: el.value ?? ''});
                        } else if (annotation.fieldType === 'Btn' && annotation.checkBox) {
                            fieldValues.push({name: annotation.fieldName, type: 'checkbox', checked: el.checked});
                        }
                    });
                });

                // Charger les bytes du PDF original
                let pdfBytes;
                if (typeof pdfSource === 'string') {
                    const resp = await fetch(pdfSource);
                    pdfBytes = await resp.arrayBuffer();
                } else if (pdfSource.data) {
                    pdfBytes = pdfSource.data.buffer ?? pdfSource.data;
                }

                if (pdfBytes && fieldValues.length > 0) {
                    const pdfDoc = await PDFDocument.load(pdfBytes, {ignoreEncryption: true});
                    const form = pdfDoc.getForm();
                    for (const field of fieldValues) {
                        try {
                            if (field.type === 'text') {
                                form.getTextField(field.name).setText(field.value);
                            } else if (field.type === 'checkbox') {
                                const cb = form.getCheckBox(field.name);
                                field.checked ? cb.check() : cb.uncheck();
                            }
                        } catch (_) {
                            // Champ introuvable, ignorer
                        }
                    }
                    const modifiedBytes = await pdfDoc.save();
                    const blob = new Blob([modifiedBytes], {type: 'application/pdf'});
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename || 'document.pdf';
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(url), 30000);
                    return;
                }
            } catch (err) {
                console.error('[PDF] Erreur mise à jour formulaire:', err);
                // Fallback téléchargement direct
            }
        }

        // Téléchargement direct (mode natif ou fallback)
        const link = document.createElement('a');
        if (typeof pdfSource === 'string') {
            link.href = pdfSource;
        } else if (pdfSource.data) {
            const blob = new Blob([pdfSource.data], {type: 'application/pdf'});
            link.href = URL.createObjectURL(blob);
        }
        link.download = filename || 'document.pdf';
        link.click();
    }, [pdfSource, filename, renderMode]);

    // Ref pour le conteneur du PDF (pour le focus clavier)
    const containerRef = useRef(null);
    // Page actuellement visible (pour la navigation clavier) - state pour réactivité
    const [currentPage, setCurrentPage] = useState(0);
    // Map pour stocker la visibilité de chaque page
    const pageVisibilityRef = useRef(new Map());

    // Observer pour détecter quelle page est visible
    useEffect(() => {
        if (renderMode !== 'pdfjs' || !numPages || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const pageIndex = parseInt(entry.target.dataset.pageIndex, 10);
                    pageVisibilityRef.current.set(pageIndex, entry.intersectionRatio);
                });

                // Trouver la page avec le plus grand ratio de visibilité
                let maxRatio = 0;
                let mostVisiblePage = 0;
                pageVisibilityRef.current.forEach((ratio, pageIndex) => {
                    if (ratio > maxRatio) {
                        maxRatio = ratio;
                        mostVisiblePage = pageIndex;
                    }
                });

                setCurrentPage(mostVisiblePage);
            },
            {
                root: containerRef.current,
                threshold: [0, 0.25, 0.5, 0.75, 1],
            },
        );

        // Observer tous les canvas après un petit délai pour s'assurer qu'ils sont montés
        const timeoutId = setTimeout(() => {
            canvasRefs.current.forEach((canvas, index) => {
                if (canvas) {
                    canvas.dataset.pageIndex = index;
                    observer.observe(canvas);
                }
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
            pageVisibilityRef.current.clear();
        };
    }, [renderMode, numPages, loading]);

    // Fonction pour scroller vers une page spécifique
    const scrollToPage = useCallback((pageIndex) => {
        const canvas = canvasRefs.current[pageIndex];
        if (canvas) {
            canvas.scrollIntoView({behavior: 'instant', block: 'start'});
        }
    }, []);

    // Raccourcis clavier (uniquement en mode pdfjs)
    useEffect(() => {
        if (!open || renderMode !== 'pdfjs') return;

        const handleKeyDown = (e) => {
            // Ignorer si on est dans un champ de saisie
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key) {
                case 'ArrowLeft':
                case 'PageUp':
                    e.preventDefault();
                    if (currentPage > 0) {
                        scrollToPage(currentPage - 1);
                    }
                    break;
                case 'ArrowRight':
                case 'PageDown':
                    e.preventDefault();
                    if (numPages && currentPage < numPages - 1) {
                        scrollToPage(currentPage + 1);
                    }
                    break;
                case 'Home':
                    e.preventDefault();
                    scrollToPage(0);
                    break;
                case 'End':
                    e.preventDefault();
                    if (numPages) scrollToPage(numPages - 1);
                    break;
                case '+':
                case '=':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        zoomIn();
                    }
                    break;
                case '-':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        zoomOut();
                    }
                    break;
                case '0':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        setScale(PDF_DPI_SCALE);
                    }
                    break;
                case 'Escape':
                    handleClose();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, zoomIn, zoomOut, handleClose, renderMode, numPages, scrollToPage, currentPage]);

    // Gestes tactiles : pinch-to-zoom + swipe pour navigation
    const lastTouchDistance = useRef(null);
    const touchStartRef = useRef(null);
    const lastTapRef = useRef(0);

    const handleTouchStart = useCallback((e) => {
        // Pinch-to-zoom (2 doigts)
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            lastTouchDistance.current = Math.hypot(dx, dy);
            touchStartRef.current = null; // Annuler le swipe si pinch
        }
        // Swipe / tap (1 doigt)
        else if (e.touches.length === 1) {
            touchStartRef.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
                time: Date.now(),
            };
        }
    }, []);

    const handleTouchMove = useCallback((e) => {
        // Pinch-to-zoom
        if (e.touches.length === 2 && lastTouchDistance.current !== null) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const distance = Math.hypot(dx, dy);

            const delta = distance - lastTouchDistance.current;
            const scaleFactor = 0.005; // Sensibilité du zoom

            if (Math.abs(delta) > 5) {
                setScale((prev) => {
                    const newScale = prev + delta * scaleFactor;
                    return Math.max(0.5, Math.min(3, newScale));
                });
                lastTouchDistance.current = distance;
            }
        }
    }, []);

    const handleTouchEnd = useCallback(
        (e) => {
            // Pinch-to-zoom terminé
            if (lastTouchDistance.current !== null) {
                lastTouchDistance.current = null;
                return;
            }

            // Gestion du swipe et double-tap (1 doigt)
            if (touchStartRef.current && e.changedTouches.length === 1) {
                const touch = e.changedTouches[0];
                const deltaX = touch.clientX - touchStartRef.current.x;
                const deltaY = touch.clientY - touchStartRef.current.y;
                const deltaTime = Date.now() - touchStartRef.current.time;

                const absX = Math.abs(deltaX);
                const absY = Math.abs(deltaY);

                // Swipe horizontal (désactivé sur iOS/iPad pour éviter conflits avec gestes natifs)
                if (!browserCapabilities.isIOS && absX > 50 && absX > absY * 1.5 && deltaTime < 500) {
                    if (deltaX > 0 && currentPage > 0) {
                        // Swipe vers la droite = page précédente
                        scrollToPage(currentPage - 1);
                    } else if (deltaX < 0 && numPages && currentPage < numPages - 1) {
                        // Swipe vers la gauche = page suivante
                        scrollToPage(currentPage + 1);
                    }
                }
                // Double-tap pour zoom (si tap rapide sans mouvement)
                else if (absX < 10 && absY < 10 && deltaTime < 200) {
                    const now = Date.now();
                    if (now - lastTapRef.current < 300) {
                        // Double-tap détecté : toggle zoom
                        setScale((prev) => (prev === PDF_DPI_SCALE ? 2 * PDF_DPI_SCALE : PDF_DPI_SCALE));
                        lastTapRef.current = 0;
                    } else {
                        lastTapRef.current = now;
                    }
                }
            }

            touchStartRef.current = null;
        },
        [currentPage, numPages, scrollToPage, browserCapabilities.isIOS],
    );

    // Zoom avec la molette (Ctrl + scroll) - uniquement en mode pdfjs
    useEffect(() => {
        if (!open || renderMode !== 'pdfjs') return;

        const handleWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setScale((prev) => Math.max(0.25 * PDF_DPI_SCALE, Math.min(4 * PDF_DPI_SCALE, prev + delta)));
            }
        };

        // Capturer au niveau document avec capture: true pour intercepter avant le navigateur
        document.addEventListener('wheel', handleWheel, {passive: false, capture: true});
        return () => document.removeEventListener('wheel', handleWheel, {capture: true});
    }, [open, renderMode]);

    // Long press sur bouton fermer pour debug
    const longPressTimerRef = useRef(null);

    const handleCloseButtonMouseDown = useCallback(() => {
        longPressTimerRef.current = setTimeout(() => {
            setShowDebugInfo((prev) => !prev);
        }, 1000);
    }, []);

    const handleCloseButtonMouseUp = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const handleCloseButtonTouchStart = useCallback((e) => {
        e.stopPropagation(); // Éviter les conflits avec les gestes tactiles
        longPressTimerRef.current = setTimeout(() => {
            setShowDebugInfo((prev) => !prev);
        }, 1000);
    }, []);

    const handleCloseButtonTouchEnd = useCallback((e) => {
        e.stopPropagation();
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    height: fullScreen ? '100dvh' : '90vh',
                    maxHeight: fullScreen ? '100dvh' : '90vh',
                },
            }}
        >
            <AppBar
                position="static"
                color="default"
                elevation={1}
                sx={{
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                }}
            >
                {renderMode === 'pdfjs' ? (
                    <Box sx={{width: '100%'}}>
                        {/* Niveau 1 : Titre/Sous-titre + Bouton fermer */}
                        <Toolbar sx={{minHeight: '48px !important'}}>
                            <Box sx={{flex: 1, minWidth: 0, py: 1}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        m: '0 !important',
                                        fontWeight: 'bold',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {title}
                                </Typography>
                                {subtitle && (
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{
                                            m: '0 !important',
                                            color: 'text.secondary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>

                            {/* Fermer */}
                            <Tooltip title="Fermer">
                                <IconButton
                                    edge="end"
                                    onClick={handleClose}
                                    onMouseDown={handleCloseButtonMouseDown}
                                    onMouseUp={handleCloseButtonMouseUp}
                                    onMouseLeave={handleCloseButtonMouseUp}
                                    onTouchStart={handleCloseButtonTouchStart}
                                    onTouchEnd={handleCloseButtonTouchEnd}
                                    onTouchCancel={handleCloseButtonTouchEnd}
                                    aria-label="close"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Toolbar>

                        {/* Niveau 2 : Actions (zoom, compteur, téléchargement) */}
                        <Toolbar
                            sx={{
                                minHeight: '48px !important',
                                borderTop: 1,
                                borderColor: 'divider',
                                justifyContent: 'flex-end',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            {/* Contrôles de zoom */}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                <Tooltip title="Zoom arrière">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={zoomOut}
                                            disabled={scale <= 0.25 * PDF_DPI_SCALE}
                                        >
                                            <ZoomOutIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Typography variant="body2" sx={{minWidth: 45, textAlign: 'center'}}>
                                    {Math.round((scale / PDF_DPI_SCALE) * 100)}%
                                </Typography>
                                <Tooltip title="Zoom avant">
                                    <span>
                                        <IconButton size="small" onClick={zoomIn} disabled={scale >= 4 * PDF_DPI_SCALE}>
                                            <ZoomInIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>

                            {/* Nombre de pages */}
                            {numPages && (
                                <Typography variant="body2" sx={{color: 'text.secondary'}}>
                                    Page {currentPage + 1} / {numPages}
                                </Typography>
                            )}

                            {/* Téléchargement */}
                            <Tooltip title="Télécharger">
                                <IconButton onClick={handleDownload}>
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </Toolbar>
                    </Box>
                ) : (
                    <Toolbar sx={{flexWrap: 'wrap', gap: 1}}>
                        <Box sx={{flex: 1, minWidth: 0, py: 1}}>
                            <Typography
                                component="div"
                                sx={{
                                    m: '0 !important',
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{
                                        m: '0 !important',
                                        color: 'text.secondary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>

                        {/* Téléchargement */}
                        <Tooltip title="Télécharger">
                            <IconButton onClick={handleDownload}>
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>

                        {/* Fermer */}
                        <Tooltip title="Fermer">
                            <IconButton
                                edge="end"
                                onClick={handleClose}
                                onMouseDown={handleCloseButtonMouseDown}
                                onMouseUp={handleCloseButtonMouseUp}
                                onMouseLeave={handleCloseButtonMouseUp}
                                onTouchStart={handleCloseButtonTouchStart}
                                onTouchEnd={handleCloseButtonTouchEnd}
                                onTouchCancel={handleCloseButtonTouchEnd}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                )}
            </AppBar>

            {/* Bandeau d'information (ex. : nom de fichier non personnalisable ici) */}
            {notice && (
                <Alert
                    severity="info"
                    sx={{borderRadius: 0, alignItems: 'center'}}
                    action={
                        notice.onAction ? (
                            <Button color="inherit" size="small" onClick={notice.onAction}>
                                {notice.action_label}
                            </Button>
                        ) : undefined
                    }
                >
                    {notice.message}
                </Alert>
            )}

            {/* Debug info */}
            {showDebugInfo && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 70,
                        right: 16,
                        zIndex: 9999,
                        bgcolor: 'rgba(0, 0, 0, 0.95)',
                        color: 'white',
                        p: 2,
                        borderRadius: 1,
                        maxWidth: 600,
                        maxHeight: 'calc(100vh - 150px)',
                        overflow: 'auto',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                    }}
                >
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="caption" component="div" sx={{fontWeight: 'bold', fontSize: '13px'}}>
                            🔧 Debug Info
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setShowDebugInfo(false)}
                            sx={{color: 'white', p: 0.5}}
                            aria-label="fermer debug"
                        >
                            <CloseIcon sx={{fontSize: 16}} />
                        </IconButton>
                    </Box>

                    <Box sx={{mb: 2, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.3)'}}>
                        <Typography variant="caption" component="div">
                            <strong>Mode de rendu:</strong> {renderMode}
                        </Typography>
                        <Typography variant="caption" component="div">
                            <strong>Worker PDF.js:</strong> {isProbablyChromeAndroid ? 'Local (Chrome Android)' : 'CDN'}
                        </Typography>
                        <Typography variant="caption" component="div">
                            <strong>Support PDF natif:</strong> {browserCapabilities.supportsNativePdf ? 'Oui' : 'Non'}
                        </Typography>
                        <Typography variant="caption" component="div">
                            <strong>Détecté mobile:</strong> {browserCapabilities.isMobileDevice ? 'Oui' : 'Non'}
                        </Typography>
                        <Typography variant="caption" component="div">
                            <strong>Écran tactile:</strong> {navigator.maxTouchPoints > 0 ? 'Oui' : 'Non'} (
                            {navigator.maxTouchPoints} points)
                        </Typography>
                        <Typography variant="caption" component="div">
                            <strong>Résolution:</strong> {window.innerWidth} x {window.innerHeight}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{mt: 0.5, wordBreak: 'break-all'}}>
                            <strong>User Agent:</strong> {navigator.userAgent}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" component="div" sx={{fontWeight: 'bold', mb: 0.5}}>
                            📝 Logs ({debugLogs.length})
                        </Typography>
                        <Box sx={{maxHeight: '300px', overflow: 'auto'}}>
                            {debugLogs.length === 0 ? (
                                <Typography variant="caption" sx={{opacity: 0.6}}>
                                    Aucun log pour le moment
                                </Typography>
                            ) : (
                                debugLogs.map((log, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            mb: 0.5,
                                            p: 0.5,
                                            bgcolor:
                                                log.type === 'error'
                                                    ? 'rgba(255, 0, 0, 0.2)'
                                                    : log.type === 'warn'
                                                      ? 'rgba(255, 165, 0, 0.2)'
                                                      : 'transparent',
                                            borderLeft:
                                                log.type === 'error'
                                                    ? '3px solid #ff5555'
                                                    : log.type === 'warn'
                                                      ? '3px solid #ffaa55'
                                                      : '3px solid #55aa55',
                                            borderRadius: '2px',
                                        }}
                                    >
                                        <Typography variant="caption" component="div" sx={{opacity: 0.7}}>
                                            {log.timestamp}
                                        </Typography>
                                        <Typography variant="caption" component="div" sx={{wordBreak: 'break-word'}}>
                                            {log.message}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            <Box
                ref={containerRef}
                onTouchStart={renderMode === 'pdfjs' ? handleTouchStart : undefined}
                onTouchMove={renderMode === 'pdfjs' ? handleTouchMove : undefined}
                onTouchEnd={renderMode === 'pdfjs' ? handleTouchEnd : undefined}
                tabIndex={0}
                sx={{
                    flex: 1,
                    overflow: renderMode === 'pdfjs' ? 'auto' : 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: '#525659',
                    py: renderMode === 'pdfjs' ? 2 : 0,
                    outline: 'none',
                    touchAction: 'pan-x pan-y',
                }}
            >
                {/* Mode object pour les formulaires interactifs (meilleur support) */}
                {renderMode === 'object' && iframeUrl && (
                    <object
                        data={iframeUrl}
                        type="application/pdf"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }}
                        title={title}
                    >
                        {/* Fallback si object ne fonctionne pas */}
                        <iframe
                            src={iframeUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                            }}
                            title={title}
                        />
                    </object>
                )}

                {/* Mode iframe pour les PDF classiques sur desktop */}
                {renderMode === 'iframe' && iframeUrl && (
                    <iframe
                        src={iframeUrl}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }}
                        title={title}
                    />
                )}

                {/* Message pour les formulaires interactifs sur mobile (pdfjs utilisé) */}
                {interactive && renderMode === 'pdfjs' && (
                    <Box
                        sx={{
                            bgcolor: 'warning.main',
                            color: 'warning.contrastText',
                            p: 2,
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <Typography variant="body2">
                            Ce PDF contient un formulaire interactif. Pour le remplir, veuillez le télécharger et
                            l'ouvrir dans une application PDF.
                        </Typography>
                    </Box>
                )}

                {/* Mode pdfjs (fallback universel) */}
                {renderMode === 'pdfjs' && loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'white',
                        }}
                    >
                        <CircularProgress color="inherit" />
                        <Typography variant="body1" sx={{mt: 2}}>
                            Chargement du PDF...
                        </Typography>
                    </Box>
                )}

                {renderMode === 'pdfjs' && error && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'white',
                            p: 4,
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {error}
                        </Typography>
                        <Typography variant="body2">
                            <a
                                href={typeof pdfSource === 'string' ? pdfSource : '#'}
                                download={filename}
                                style={{color: '#90caf9'}}
                                onClick={handleDownload}
                            >
                                Cliquez ici pour télécharger le document
                            </a>
                        </Typography>
                    </Box>
                )}

                {/* Rendu PDF avec pdf.js natif - toutes les pages */}
                {renderMode === 'pdfjs' && pdfSource && !error && !loading && numPages && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        {Array.from({length: numPages}, (_, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                    bgcolor: 'white',
                                    // Cacher les pages pas encore rendues mais garder l'espace
                                    visibility: index < renderedPages ? 'visible' : 'hidden',
                                    // Utiliser les dimensions de la première page pour les placeholders
                                    minHeight: index < renderedPages ? 'auto' : pageDimensions?.height || 200,
                                    minWidth: index < renderedPages ? 'auto' : pageDimensions?.width || 150,
                                }}
                            >
                                <canvas
                                    ref={(el) => {
                                        canvasRefs.current[index] = el;
                                    }}
                                    style={{display: 'block'}}
                                />
                                {/* Couche TextLayer pdf.js : texte sélectionnable */}
                                <div
                                    ref={(el) => {
                                        textLayerDivRefs.current[index] = el;
                                    }}
                                    className="textLayer"
                                />
                                {/* Couche AnnotationLayer pdf.js : champs de formulaire + liens */}
                                <div
                                    ref={(el) => {
                                        annotationDivRefs.current[index] = el;
                                    }}
                                    className="annotationLayer"
                                />
                            </Box>
                        ))}

                        {/* Indicateur de chargement des pages restantes */}
                        {renderedPages < numPages && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    py: 3,
                                    color: 'white',
                                }}
                            >
                                <CircularProgress color="inherit" size={32} />
                                <Typography variant="body2" sx={{mt: 1}}>
                                    Chargement page {renderedPages + 1} / {numPages}...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default PdfViewerModal;
